import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Plus, Mail, Phone, Pencil, Trash2, X, Plug, Search, Check } from "lucide-react";
import Shell, { Breadcrumbs } from "@/components/Shell";
import { Card, Btn, Input, Label, Spinner, StatusBadge, IconBtn } from "@/components/ui-kit";
import api from "@/lib/api";
import { useConfirm } from "@/lib/confirm";

const XeroLinkCard = ({ client, onChange }) => {
  const [xeroEnabled, setXeroEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/xero/status").then((r) => setXeroEnabled(!!r.data.connected)).catch(() => setXeroEnabled(false));
  }, []);

  const search = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/xero/contacts", { params: { q: q || undefined } });
      setResults(data.contacts || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load Xero contacts");
      setResults([]);
    } finally { setLoading(false); }
  };

  const link = async (c) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/clients/${client.id}/xero-link`, { xero_contact_id: c.id, xero_contact_name: c.name });
      setOpen(false); setQ(""); setResults([]);
      onChange();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to link");
    } finally { setBusy(false); }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await api.delete(`/clients/${client.id}/xero-link`);
      onChange();
    } finally { setBusy(false); }
  };

  if (!xeroEnabled) return null;

  return (
    <Card className="p-6 mb-6 border-sky-100 bg-sky-50/30" data-testid="client-xero-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-sky-100 grid place-items-center"><Plug className="w-4 h-4 text-sky-700" /></div>
          <div>
            <h2 className="font-display text-base font-semibold text-zinc-950">Xero contact</h2>
            {client.xero_contact_id ? (
              <p className="text-xs text-emerald-700 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Linked to <strong className="ml-1">{client.xero_contact_name || client.xero_contact_id}</strong>
              </p>
            ) : (
              <p className="text-xs text-zinc-500">Not linked yet — invoices can&rsquo;t be pushed to Xero until you link a contact.</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {client.xero_contact_id ? (
            <>
              <Btn size="sm" variant="secondary" onClick={() => setOpen((o) => !o)} data-testid="xero-relink-btn">{open ? "Close" : "Re-link"}</Btn>
              <Btn size="sm" variant="secondary" onClick={unlink} disabled={busy} data-testid="xero-unlink-btn">Unlink</Btn>
            </>
          ) : (
            <Btn size="sm" onClick={() => setOpen((o) => !o)} data-testid="xero-link-open-btn">{open ? "Close" : "Link to Xero"}</Btn>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-4 border-t border-sky-100 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search Xero contacts by name…"
                className="!pl-9"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
                data-testid="xero-search-input"
              />
            </div>
            <Btn size="sm" onClick={search} disabled={loading} data-testid="xero-search-btn">{loading ? "…" : "Search"}</Btn>
          </div>
          {error && <p className="text-xs text-red-700 mb-2">{error}</p>}
          {results.length > 0 && (
            <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100 max-h-64 overflow-auto bg-white">
              {results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => link(c)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2.5 hover:bg-zinc-50 transition-colors flex items-center justify-between gap-3"
                  data-testid={`xero-result-${c.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-950 truncate">{c.name}</p>
                    {c.email && <p className="text-xs text-zinc-500 truncate">{c.email}</p>}
                  </div>
                  <Plus className="w-4 h-4 text-zinc-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
          {!loading && !error && results.length === 0 && q && (
            <p className="text-xs text-zinc-500 italic">No matches — refine your search or add the contact in Xero first.</p>
          )}
        </div>
      )}
    </Card>
  );
};

const ContactModal = ({ open, onClose, contact, onSave }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  useEffect(() => {
    if (open) setForm(contact || { name: "", email: "", phone: "" });
  }, [open, contact]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in" data-testid="contact-modal">
      <div className="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between p-6 pb-2">
          <h2 className="font-display text-2xl font-semibold">{contact ? "Edit Contact" : "Add Contact"}</h2>
          <IconBtn onClick={onClose}><X className="w-4 h-4" /></IconBtn>
        </div>
        <form
          onSubmit={async (e) => { e.preventDefault(); await onSave(form); onClose(); }}
          className="p-6 pt-2 space-y-4"
        >
          <div><Label required>Name</Label><Input required data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" data-testid="contact-save-btn">Save</Btn>
          </div>
        </form>
      </div>
    </div>
  );
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [client, setClient] = useState(null);
  const [editContact, setEditContact] = useState(null); // null / object / "new"
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/clients/${id}`);
    setClient(data);
  };
  useEffect(() => { load(); }, [id]);

  const saveContact = async (data) => {
    if (editContact && editContact !== "new") {
      await api.put(`/clients/${id}/contacts/${editContact.id}`, data);
    } else {
      await api.post(`/clients/${id}/contacts`, data);
    }
    await load();
  };

  const deleteContact = async (cid) => {
    if (!(await confirm({ title: "Delete this contact?", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/clients/${id}/contacts/${cid}`);
    await load();
  };

  if (!client) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <Breadcrumbs items={[{ to: "/clients", label: "Clients" }, { label: client.name }]} />
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-bold text-zinc-950">{client.name}</h1>
          <p className="text-sm text-zinc-500 mt-1">Client Details</p>
        </div>
        <Btn variant="secondary" onClick={() => navigate(`/clients/${id}/edit`)} data-testid="edit-client-btn">
          <Pencil className="w-4 h-4" />Edit Client
        </Btn>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold text-zinc-950 mb-4">Contact Information</h2>
          {client.email && <div className="flex items-center gap-2 text-sm text-zinc-700 mb-2"><Mail className="w-4 h-4 text-zinc-400" />{client.email}</div>}
          {client.phone && <div className="flex items-center gap-2 text-sm text-zinc-700 mb-4"><Phone className="w-4 h-4 text-zinc-400" />{client.phone}</div>}
          {client.address && (
            <div className="pt-4 border-t border-zinc-100">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2">Address</p>
              <p className="text-sm text-zinc-700 whitespace-pre-line">{client.address}</p>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold text-zinc-950 mb-4">Statistics</h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="font-display text-3xl font-bold">{client.contact_count}</p>
              <p className="text-xs text-zinc-500 mt-1">Contacts</p>
            </div>
            <div>
              <p className="font-display text-3xl font-bold">{client.project_count}</p>
              <p className="text-xs text-zinc-500 mt-1">Projects</p>
            </div>
            <div>
              <p className="font-display text-3xl font-bold">{client.quote_count}</p>
              <p className="text-xs text-zinc-500 mt-1">Quotes</p>
            </div>
          </div>
        </Card>
      </div>

      <XeroLinkCard client={client} onChange={load} />

      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-zinc-950">Contacts</h2>
          <Btn size="sm" onClick={() => { setEditContact("new"); setModalOpen(true); }} data-testid="add-contact-btn">
            <Plus className="w-3.5 h-3.5" />Add Contact
          </Btn>
        </div>
        {(!client.contacts || client.contacts.length === 0) ? (
          <p className="text-sm text-zinc-500 text-center py-6">No contacts yet</p>
        ) : (
          <div className="space-y-2">
            {client.contacts.map((ct) => (
              <div key={ct.id} data-testid={`contact-${ct.id}`} className="flex items-start justify-between gap-3 p-4 border border-zinc-100 rounded-lg">
                <div>
                  <p className="text-sm font-semibold text-zinc-950">{ct.name}</p>
                  {ct.email && <p className="text-xs text-zinc-500 mt-1"><Mail className="w-3 h-3 inline mr-1" />{ct.email}</p>}
                  {ct.phone && <p className="text-xs text-zinc-500 mt-0.5"><Phone className="w-3 h-3 inline mr-1" />{ct.phone}</p>}
                </div>
                <div className="flex gap-1">
                  <Btn size="sm" variant="secondary" onClick={() => { setEditContact(ct); setModalOpen(true); }} data-testid={`contact-edit-${ct.id}`}>Edit</Btn>
                  <IconBtn variant="destructive" onClick={() => deleteContact(ct.id)} data-testid={`contact-delete-${ct.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="font-display text-lg font-semibold text-zinc-950 mb-4">Recent Projects</h2>
        {(!client.recent_projects || client.recent_projects.length === 0) ? (
          <p className="text-sm text-zinc-500 text-center py-6">No projects yet</p>
        ) : (
          <div className="space-y-2">
            {client.recent_projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`recent-project-${p.id}`} className="flex items-center justify-between p-4 border border-zinc-100 rounded-lg hover:bg-zinc-50 transition-colors">
                <div>
                  <p className="text-sm font-semibold text-zinc-950">{p.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Job #{p.job_number}</p>
                </div>
                <StatusBadge status={p.status} />
              </Link>
            ))}
          </div>
        )}
      </Card>

      <ContactModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        contact={editContact && editContact !== "new" ? editContact : null}
        onSave={saveContact}
      />
    </Shell>
  );
};

export default ClientDetail;
